import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Send, Calendar, CheckCircle, ArrowLeft, ArrowRight, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { siteConfig, legalDisclaimer } from '@/data/mock';
import { de } from 'date-fns/locale';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { submitContact, submitBooking, getAvailableSlots } from '@/services/api';

const Contact = () => {
  const [activeTab, setActiveTab] = useState('contact');
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookingStep, setBookingStep] = useState(1);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    topic: ''
  });

  // Fetch available slots when date changes
  useEffect(() => {
    if (selectedDate && activeTab === 'booking') {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate, activeTab]);

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const slots = await getAvailableSlots(dateStr);
      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Fehler beim Laden der Zeitslots');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContact(contactForm);
      toast.success('Nachricht gesendet!', {
        description: 'Ich melde mich innerhalb von 24 Stunden bei Ihnen.'
      });
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact:', error);
      toast.error('Fehler beim Senden', {
        description: 'Bitte versuchen Sie es später erneut.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await submitBooking({
        ...bookingForm,
        date: dateStr,
        time: selectedTime
      });
      toast.success('Termin gebucht!', {
        description: `Ihr Termin am ${selectedDate?.toLocaleDateString('de-AT')} um ${selectedTime} Uhr wurde bestätigt.`
      });
      setBookingForm({ name: '', email: '', phone: '', topic: '' });
      setSelectedDate(undefined);
      setSelectedTime(null);
      setBookingStep(1);
    } catch (error) {
      console.error('Error submitting booking:', error);
      const message = error.response?.data?.detail || 'Bitte versuchen Sie es später erneut.';
      toast.error('Fehler bei der Buchung', { description: message });
    } finally {
      setSubmitting(false);
    }
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 lg:py-20 bg-[hsl(210,40%,98%)] pattern-bg relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[hsl(213,80%,35%,0.1)] text-[hsl(213,80%,35%)] text-sm font-medium mb-6">
              Ich bin für Sie da
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[hsl(215,25%,15%)] mb-6">
              Kontakt
            </h1>
            <p className="text-xl text-[hsl(215,15%,45%)] leading-relaxed">
              Schreiben Sie mir eine Nachricht oder buchen Sie direkt einen Termin für ein 
              kostenloses Erstgespräch.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white border-b border-[hsl(210,25%,92%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-[hsl(210,25%,90%)] card-hover">
              <CardContent className="p-6 flex items-center">
                <div className="w-12 h-12 rounded-xl bg-[hsl(213,80%,35%,0.1)] text-[hsl(213,80%,35%)] flex items-center justify-center mr-4">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-[hsl(215,15%,45%)] mb-1">Telefon</p>
                  <a href={`tel:${siteConfig.phone}`} className="font-semibold text-[hsl(215,25%,15%)] hover:text-[hsl(213,80%,35%)] transition-colors">
                    {siteConfig.phone}
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[hsl(210,25%,90%)] card-hover">
              <CardContent className="p-6 flex items-center">
                <div className="w-12 h-12 rounded-xl bg-[hsl(213,80%,35%,0.1)] text-[hsl(213,80%,35%)] flex items-center justify-center mr-4">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-[hsl(215,15%,45%)] mb-1">E-Mail</p>
                  <a href={`mailto:${siteConfig.email}`} className="font-semibold text-[hsl(215,25%,15%)] hover:text-[hsl(213,80%,35%)] transition-colors">
                    {siteConfig.email}
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[hsl(210,25%,90%)] card-hover">
              <CardContent className="p-6 flex items-center">
                <div className="w-12 h-12 rounded-xl bg-[hsl(213,80%,35%,0.1)] text-[hsl(213,80%,35%)] flex items-center justify-center mr-4">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-[hsl(215,15%,45%)] mb-1">Erreichbarkeit</p>
                  <p className="font-semibold text-[hsl(215,25%,15%)]">
                    {siteConfig.availability || "von 10:00 - 16:00 und nach Vereinbarung"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab Navigation */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-[hsl(210,40%,98%)] rounded-lg p-1">
              <button
                onClick={() => setActiveTab('contact')}
                className={`px-6 py-3 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'contact'
                    ? 'bg-white text-[hsl(213,80%,35%)] shadow-sm'
                    : 'text-[hsl(215,15%,45%)] hover:text-[hsl(215,25%,30%)]'
                }`}
              >
                <Mail className="w-4 h-4 inline mr-2" />
                Nachricht senden
              </button>
              <button
                onClick={() => setActiveTab('booking')}
                className={`px-6 py-3 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'booking'
                    ? 'bg-white text-[hsl(213,80%,35%)] shadow-sm'
                    : 'text-[hsl(215,15%,45%)] hover:text-[hsl(215,25%,30%)]'
                }`}
              >
                <Calendar className="w-4 h-4 inline mr-2" />
                Termin buchen
              </button>
            </div>
          </div>

          {/* Contact Form */}
          {activeTab === 'contact' && (
            <div className="max-w-2xl mx-auto">
              <Card className="border-[hsl(210,25%,90%)]">
                <CardHeader>
                  <CardTitle className="text-[hsl(215,25%,15%)]">Nachricht senden</CardTitle>
                  <p className="text-[hsl(215,15%,45%)]">
                    Beschreiben Sie kurz Ihr Anliegen. Ich melde mich innerhalb von 24 Stunden.
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          required
                          value={contactForm.name}
                          onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                          placeholder="Ihr Name"
                          className="border-[hsl(210,25%,88%)] focus:border-[hsl(213,80%,35%)] focus:ring-[hsl(213,80%,35%,0.2)]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">E-Mail *</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={contactForm.email}
                          onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                          placeholder="ihre@email.at"
                          className="border-[hsl(210,25%,88%)] focus:border-[hsl(213,80%,35%)] focus:ring-[hsl(213,80%,35%,0.2)]"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon (optional)</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                        placeholder="+43 ..."
                        className="border-[hsl(210,25%,88%)] focus:border-[hsl(213,80%,35%)] focus:ring-[hsl(213,80%,35%,0.2)]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Ihre Nachricht *</Label>
                      <Textarea
                        id="message"
                        required
                        rows={5}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                        placeholder="Beschreiben Sie kurz Ihre Situation oder Ihr Anliegen..."
                        className="border-[hsl(210,25%,88%)] focus:border-[hsl(213,80%,35%)] focus:ring-[hsl(213,80%,35%,0.2)] resize-none"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      disabled={submitting}
                      className="w-full bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white"
                    >
                      {submitting ? 'Wird gesendet...' : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Nachricht absenden
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Booking System */}
          {activeTab === 'booking' && (
            <div className="max-w-4xl mx-auto">
              {/* Progress Steps */}
              <div className="flex items-center justify-center mb-12">
                {[1, 2, 3].map((step) => (
                  <React.Fragment key={step}>
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all ${
                      bookingStep >= step 
                        ? 'bg-[hsl(213,80%,35%)] text-white'
                        : 'bg-[hsl(210,25%,92%)] text-[hsl(215,15%,45%)]'
                    }`}>
                      {bookingStep > step ? <CheckCircle className="w-5 h-5" /> : step}
                    </div>
                    {step < 3 && (
                      <div className={`w-20 h-1 mx-2 rounded ${
                        bookingStep > step ? 'bg-[hsl(213,80%,35%)]' : 'bg-[hsl(210,25%,92%)]'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>

              <Card className="border-[hsl(210,25%,90%)]">
                <CardContent className="p-8">
                  {/* Step 1: Select Date */}
                  {bookingStep === 1 && (
                    <div>
                      <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mb-2">Datum wählen</h3>
                      <p className="text-[hsl(215,15%,45%)] mb-6">Wählen Sie einen Termin für Ihr kostenloses Erstgespräch.</p>
                      
                      <div className="flex justify-center">
                        <CalendarComponent
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          locale={de}
                          disabled={(date) => isWeekend(date) || isPastDate(date)}
                          className="rounded-lg border border-[hsl(210,25%,90%)] p-4 scale-110 origin-top"
                          classNames={{
                            months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                            month: "space-y-6",
                            caption: "flex justify-center pt-2 relative items-center text-lg",
                            caption_label: "text-base font-semibold",
                            nav_button: "h-9 w-9",
                            table: "w-full border-collapse space-y-2",
                            head_cell: "text-muted-foreground rounded-md w-12 font-medium text-sm",
                            cell: "h-12 w-12 text-center text-base p-0 relative",
                            day: "h-11 w-11 p-0 font-normal text-base",
                            day_selected: "bg-[hsl(213,80%,35%)] text-white hover:bg-[hsl(213,80%,30%)]",
                            day_today: "bg-[hsl(210,40%,95%)] text-[hsl(213,80%,35%)] font-semibold",
                          }}
                        />
                      </div>

                      <div className="mt-8 flex justify-end">
                        <Button 
                          onClick={() => setBookingStep(2)}
                          disabled={!selectedDate}
                          className="bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white"
                        >
                          Weiter
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Select Time */}
                  {bookingStep === 2 && (
                    <div>
                      <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mb-2">Uhrzeit wählen</h3>
                      <p className="text-[hsl(215,15%,45%)] mb-6">
                        Verfügbare Zeiten für {selectedDate?.toLocaleDateString('de-AT', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </p>
                      
                      {loadingSlots ? (
                        <div className="text-center py-8 text-[hsl(215,15%,45%)]">Lade verfügbare Zeiten...</div>
                      ) : (
                        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                          {availableSlots.map((slot) => (
                            <button
                              key={slot.time}
                              onClick={() => slot.available && setSelectedTime(slot.time)}
                              disabled={!slot.available}
                              className={`p-4 rounded-lg border-2 font-medium transition-all ${
                                selectedTime === slot.time
                                  ? 'border-[hsl(213,80%,35%)] bg-[hsl(213,80%,35%,0.1)] text-[hsl(213,80%,35%)]'
                                  : slot.available
                                    ? 'border-[hsl(210,25%,90%)] hover:border-[hsl(213,80%,35%,0.5)] text-[hsl(215,25%,30%)]'
                                    : 'border-[hsl(210,25%,92%)] bg-[hsl(210,25%,96%)] text-[hsl(215,15%,60%)] cursor-not-allowed line-through'
                              }`}
                            >
                              {slot.time} Uhr
                            </button>
                          ))}
                        </div>
                      )}

                      <div className="mt-8 flex justify-between">
                        <Button 
                          variant="outline" 
                          onClick={() => setBookingStep(1)}
                          className="border-[hsl(210,25%,88%)]"
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" />
                          Zurück
                        </Button>
                        <Button 
                          onClick={() => setBookingStep(3)}
                          disabled={!selectedTime}
                          className="bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white"
                        >
                          Weiter
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Personal Details */}
                  {bookingStep === 3 && (
                    <div>
                      <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mb-2">Ihre Daten</h3>
                      <p className="text-[hsl(215,15%,45%)] mb-6">
                        Termin: {selectedDate?.toLocaleDateString('de-AT', { weekday: 'long', day: 'numeric', month: 'long' })} um {selectedTime} Uhr
                      </p>
                      
                      <form onSubmit={handleBookingSubmit} className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="booking-name">Name *</Label>
                            <Input
                              id="booking-name"
                              required
                              value={bookingForm.name}
                              onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                              placeholder="Ihr Name"
                              className="border-[hsl(210,25%,88%)] focus:border-[hsl(213,80%,35%)] focus:ring-[hsl(213,80%,35%,0.2)]"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="booking-email">E-Mail *</Label>
                            <Input
                              id="booking-email"
                              type="email"
                              required
                              value={bookingForm.email}
                              onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                              placeholder="ihre@email.at"
                              className="border-[hsl(210,25%,88%)] focus:border-[hsl(213,80%,35%)] focus:ring-[hsl(213,80%,35%,0.2)]"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="booking-phone">Telefon *</Label>
                          <Input
                            id="booking-phone"
                            type="tel"
                            required
                            value={bookingForm.phone}
                            onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                            placeholder="+43 ..."
                            className="border-[hsl(210,25%,88%)] focus:border-[hsl(213,80%,35%)] focus:ring-[hsl(213,80%,35%,0.2)]"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="booking-topic">Kurze Beschreibung Ihres Anliegens</Label>
                          <Textarea
                            id="booking-topic"
                            rows={3}
                            value={bookingForm.topic}
                            onChange={(e) => setBookingForm({...bookingForm, topic: e.target.value})}
                            placeholder="Damit ich mich auf unser Gespräch vorbereiten kann..."
                            className="border-[hsl(210,25%,88%)] focus:border-[hsl(213,80%,35%)] focus:ring-[hsl(213,80%,35%,0.2)] resize-none"
                          />
                        </div>

                        <div className="p-4 bg-[hsl(35,90%,95%)] border border-[hsl(35,70%,80%)] rounded-lg">
                          <div className="flex items-start">
                            <Shield className="w-5 h-5 text-[hsl(35,90%,40%)] mr-3 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-[hsl(35,50%,30%)]">
                              <strong>Hinweis:</strong> Dieses Gespräch stellt keine Rechtsberatung dar.
                            </p>
                          </div>
                        </div>

                        <div className="flex justify-between">
                          <Button 
                            type="button"
                            variant="outline" 
                            onClick={() => setBookingStep(2)}
                            className="border-[hsl(210,25%,88%)]"
                          >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Zurück
                          </Button>
                          <Button 
                            type="submit"
                            disabled={submitting}
                            className="bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white"
                          >
                            {submitting ? 'Wird gebucht...' : (
                              <>
                                <Calendar className="w-4 h-4 mr-2" />
                                Termin bestätigen
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Contact;
